import { addDays, diffDays, isScheduledOn, weekStart } from "./schedule";
import type { CompletionMap, HabitSchedule, StreakResult } from "./types";

/**
 * Streak logic — product heart. PURE: input (schedule,
 * completions, today) and output (current streak, record). Nothing is stored.
 *
 * Rules (see PRODUCT_PLAN.md):
 * - `done` adds; `skipped` (freeze) PRESERVES streak without adding.
 * - Today unmarked does not break streak (pending until end of day).
 * - daily/weekdays: unit is days; only scheduled days count.
 * - times_per_week: unit is weeks; completed week = (done+skipped) ≥ target.
 *   Current week adds if already met and does not break while achievable.
 * - STREAK PROTECTOR (optional): a PAST, scheduled, unmarked day with
 *   spent protector behaves EXACTLY as `skipped` — preserves without
 *   adding. Without `protectedDays` (default) calculation is byte-for-byte
 *   historical. See `habits/streak-protector.ts`.
 */

const DAYS_PER_WEEK = 7;

/** Calculation options. Omitted ⇒ exact historical behavior. */
export interface StreakOptions {
  /**
   * Days (day-keys) of THIS habit with spent protector. Treated as
   * `skipped`: preserve streak without adding.
   */
  protectedDays?: ReadonlySet<string>;
}

const NO_PROTECTED_DAYS: ReadonlySet<string> = new Set<string>();

function earliestKey(completions: CompletionMap): string | undefined {
  let min: string | undefined;
  for (const key of completions.keys()) {
    if (min === undefined || key < min) {
      min = key;
    }
  }
  return min;
}

function dailyStreaks(
  schedule: HabitSchedule,
  completions: CompletionMap,
  todayKey: string,
  protectedDays: ReadonlySet<string>,
): StreakResult {
  const first = earliestKey(completions);
  if (!first || first > todayKey) {
    return { current: 0, best: 0, unit: "days" };
  }
  let run = 0;
  let best = 0;
  for (let day = first; day <= todayKey; day = addDays(day, 1)) {
    if (!isScheduledOn(schedule, day)) {
      continue; // unscheduled day: neither increments nor breaks
    }
    const status = completions.get(day);
    if (status === "done") {
      run += 1;
      best = Math.max(best, run);
    } else if (status === "skipped") {
      // freeze: preserves without adding
    } else if (protectedDays.has(day)) {
      // PROTECTOR spent on that day: behaves like `skipped`.
    } else if (day !== todayKey) {
      run = 0; // scheduled unmarked day (past) → breaks
    }
    // unmarked today: pending, does not break streak
  }
  return { current: run, best, unit: "days" };
}

/**
 * Counts days meeting target per week (ISO Monday). A protected
 * day counts same as `skipped` (preserves the week).
 */
function weeklyCounts(
  completions: CompletionMap,
  protectedDays: ReadonlySet<string>,
): Map<string, number> {
  const counts = new Map<string, number>();
  const seen = new Set<string>();
  for (const [day, status] of completions) {
    if (status === "done" || status === "skipped") {
      const week = weekStart(day);
      counts.set(week, (counts.get(week) ?? 0) + 1);
      seen.add(day);
    }
  }
  for (const day of protectedDays) {
    if (seen.has(day)) {
      continue; // already counted by check-in: protector does not duplicate
    }
    const week = weekStart(day);
    counts.set(week, (counts.get(week) ?? 0) + 1);
  }
  return counts;
}

function weeklyStreaks(
  schedule: HabitSchedule,
  completions: CompletionMap,
  todayKey: string,
  protectedDays: ReadonlySet<string>,
): StreakResult {
  const first = earliestKey(completions);
  if (!first || first > todayKey) {
    return { current: 0, best: 0, unit: "weeks" };
  }
  const target = schedule.timesPerWeek;
  const counts = weeklyCounts(completions, protectedDays);
  const thisWeek = weekStart(todayKey);

  let run = 0;
  let best = 0;
  for (let week = weekStart(first); week < thisWeek; week = addDays(week, DAYS_PER_WEEK)) {
    if ((counts.get(week) ?? 0) >= target) {
      run += 1;
      best = Math.max(best, run);
    } else {
      run = 0;
    }
  }

  // Current week: adds if met; if still achievable does not break; if
  // impossible (remaining days < needed), previous streak dies.
  const countNow = counts.get(thisWeek) ?? 0;
  let current = run;
  if (countNow >= target) {
    current = run + 1;
    best = Math.max(best, current);
  } else {
    const daysLeft = DAYS_PER_WEEK - diffDays(thisWeek, todayKey); // incluye hoy
    if (target - countNow > daysLeft) {
      current = 0;
    }
  }
  return { current, best, unit: "weeks" };
}

export function computeStreaks(
  schedule: HabitSchedule,
  completions: CompletionMap,
  todayKey: string,
  options: StreakOptions = {},
): StreakResult {
  const protectedDays = options.protectedDays ?? NO_PROTECTED_DAYS;
  return schedule.kind === "times_per_week"
    ? weeklyStreaks(schedule, completions, todayKey, protectedDays)
    : dailyStreaks(schedule, completions, todayKey, protectedDays);
}

// ─── Derived statistics ─────────────────────────────────────────────────────

export interface ComplianceResult {
  /** 0–1. `done` days over scheduled days (skipped excluded from both). */
  rate: number;
  done: number;
  scheduled: number;
}

/**
 * Compliance in range [fromKey, toKey] (both inclusive).
 * - daily/weekdays: done / unfrozen scheduled days in range.
 * - times_per_week: done / (target × weeks in range), clamped to 1.
 */
export function computeCompliance(
  schedule: HabitSchedule,
  completions: CompletionMap,
  fromKey: string,
  toKey: string,
): ComplianceResult {
  if (fromKey > toKey) {
    return { rate: 0, done: 0, scheduled: 0 };
  }
  if (schedule.kind === "times_per_week") {
    const weeks = Math.ceil((diffDays(fromKey, toKey) + 1) / DAYS_PER_WEEK);
    const scheduled = weeks * schedule.timesPerWeek;
    let done = 0;
    for (const [day, status] of completions) {
      if (day >= fromKey && day <= toKey && status === "done") {
        done += 1;
      }
    }
    return { rate: scheduled === 0 ? 0 : Math.min(1, done / scheduled), done, scheduled };
  }
  let done = 0;
  let scheduled = 0;
  for (let day = fromKey; day <= toKey; day = addDays(day, 1)) {
    if (!isScheduledOn(schedule, day)) {
      continue;
    }
    const status = completions.get(day);
    if (status === "skipped") {
      continue; // freeze: excluded from denominator
    }
    scheduled += 1;
    if (status === "done") {
      done += 1;
    }
  }
  return { rate: scheduled === 0 ? 0 : done / scheduled, done, scheduled };
}

/** Current week progress for x-per-week habits. */
export function weeklyProgress(
  completions: CompletionMap,
  todayKey: string,
  target: number,
): { count: number; target: number } {
  const start = weekStart(todayKey);
  const end = addDays(start, DAYS_PER_WEEK - 1);
  let count = 0;
  for (const [day, status] of completions) {
    if (day >= start && day <= end && (status === "done" || status === "skipped")) {
      count += 1;
    }
  }
  return { count, target };
}
